import { DocListNode } from './DocListNode';
import { CompanyListNode } from './CompanyListNode';
import { PromptNode } from './PromptNode';
import CustomEdge from './components/CustomEdge';
import StartNode from './StartNode';
import { nanoid } from 'nanoid';
import { CreateNode } from './CreateNode';
import { GenerateCompanyListNode } from './GenerateCompanyListNode';
import ForEachCompanyQuestionNode from './ForEachCompanyQuestion';
import ForEachCompanyNode from './ForEachCompany';
import ForEachIngestCompanyWebsite from './ForEachIngestCompanyWebsite';
import { IfElseNode } from './IfElseNode';
import { FormulaNode } from './FormulaNode';
import AgentScriptStartNode from './AgentScriptStartNode';
import OutputAggregationNode from './OutputAggregationNode';
import { AgentScriptCreateNode } from './AgentScriptCreateNode';
import PlanStepNode from './PlanStepNode';
import { AgentScriptQueryNode } from './AgentScriptQueryNode';

export const NODE_DEBUGGING = false;

export const COMPANY_LIST_PROMPT_PREFIX =
  'do not includes any extra text, using json and responding with a list of companies where each company is an element in an array of companies, and each element is an object with the key value pairs of "name", "symbol", satisfy this description of a company list: ';
export const createCompanyListPrompt = (description) =>
  `Create a list of companies that best match this description:${description}. Respond in a JSON object with elements for "name" and "symbol" for each company`;

export const NODE_TYPES = {
  AgentScriptStartNode: 'Agent Script Start Node',
  AgentScriptCreateNode: 'Agent Script Create Node',
  PlanStepNode: 'Plan Step Node',
  OutputAggregationNode: 'Output Aggregation Node',
  PlanStepQueryNode: 'Plan Step Query Node',
  AgentScriptQueryNode: 'Agent Script Query Node',

  StartNode: 'Start Node',
  CreateNode: 'Create Node',

  PromptNode: 'Question Node',
  GenerateCompanyListNode: 'Generate Company List Node',
  IfElseNode: 'If/Else Node',
  FormulaNode: 'Formula Node',

  ForEachCompanyNode: 'ForEach Company Node',

  ForEachCompanyQuestionNode: 'ForEach Company Question Node',
  ForEachIngestWebsiteNode: 'ForEach Ingest Website Node',

  CompanyListNode: 'Company List Node',
  DocListNode: 'Document List Node',
};

export const EXECUTION_NODE_TYPES = [
  NODE_TYPES.StartNode,
  NODE_TYPES.ForEachCompanyNode,
  NODE_TYPES.IfElseNode,
  NODE_TYPES.FormulaNode,
  NODE_TYPES.PromptNode,
  NODE_TYPES.GenerateCompanyListNode,
];

export const NODE_CREATION_CONTEXTS = {
  Start: 'Start Node',

  BodyTargetNode: 'Body Target Node',
  BodySourceNode: 'Body Source Node',

  ExecutionFlowNode: 'Execution Flow Node',

  SourceInputNode: 'Source Input Node',

  ForEachAction: 'ForEach Action',

  Query: 'Query',
  PlanStep: 'Plan Step',
  OutputAggregation: 'Output Aggregation',
  AgentScriptStart: 'Agent Script',

  CompanyList: 'Company List',
  DocumentList: 'Document List',
};

export const NODE_HANDLE_TYPES = {
  target: 'target',
  source: 'source',
};

export const NODE_HANDLE_IDS = {
  executionFlowTarget: 'target',
  executionFlowSource: 'source',

  executionFlowSourceTrue: 'source-yes',
  executionFlowSourceFalse: 'source-no',

  query: 'query',
  result: 'result',

  planStep: 'plan-step',
  planStepYes: 'plan-step-yes',
  planStepNo: 'plan-step-no',

  outputAggregation: 'output-aggregation',

  DocumentList: 'document-list',
  CompanyList: 'company-list',
  AdvancedSettings: 'advanced-settings',
  ForEachAction: 'foreach-action',
};

export const EDGE_TYPES = {
  custom: CustomEdge,
};

export const NODE_COMPONENT_TYPES = {
  [NODE_TYPES.StartNode]: StartNode,
  [NODE_TYPES.CreateNode]: CreateNode,

  [NODE_TYPES.AgentScriptStartNode]: AgentScriptStartNode,
  [NODE_TYPES.AgentScriptCreateNode]: AgentScriptCreateNode,

  [NODE_TYPES.AgentScriptQueryNode]: AgentScriptQueryNode,
  [NODE_TYPES.PlanStepNode]: PlanStepNode,
  [NODE_TYPES.OutputAggregationNode]: OutputAggregationNode,

  [NODE_TYPES.PromptNode]: PromptNode,
  [NODE_TYPES.IfElseNode]: IfElseNode,
  [NODE_TYPES.FormulaNode]: FormulaNode,

  [NODE_TYPES.GenerateCompanyListNode]: GenerateCompanyListNode,
  [NODE_TYPES.ForEachCompanyNode]: ForEachCompanyNode,

  [NODE_TYPES.ForEachCompanyQuestionNode]: ForEachCompanyQuestionNode,
  [NODE_TYPES.ForEachIngestWebsiteNode]: ForEachIngestCompanyWebsite,

  [NODE_TYPES.DocListNode]: DocListNode,
  [NODE_TYPES.CompanyListNode]: CompanyListNode,
};

const HANDLE_TYPE_TO_COMPONENT_LOOKUP = {
  [NODE_HANDLE_IDS.DocumentList]: NODE_TYPES.DocListNode,
  [NODE_HANDLE_IDS.CompanyList]: NODE_TYPES.CompanyListNode,
};

export const AVAILABLE_SETTING_FIELDS = ['model', 'max_context_tokens', 'max_context_chunks', 'max_answer_tokens'];

export const generateCustomInputNodeHandle = (handleType) => {
  if (handleType === NODE_HANDLE_IDS.executionFlowTarget) {
    return { name: 'Execution Flow', colorThemeName: 'executionFlowNode' };
  }
  if (handleType === NODE_HANDLE_IDS.planStep) {
    return { name: 'Plan Step', colorThemeName: 'planStepNode' };
  }
  if (handleType === NODE_HANDLE_IDS.planStepYes) {
    return { name: 'Plan Step', colorThemeName: 'planStepNode' };
  }
  if (handleType === NODE_HANDLE_IDS.planStepNo) {
    return { name: 'Plan Step', colorThemeName: 'planStepNode' };
  }
  if (handleType === NODE_HANDLE_IDS.outputAggregation) {
    return { name: 'Output Aggregation', colorThemeName: 'outputAggregationNode' };
  }
  if (handleType === NODE_HANDLE_IDS.ForEachAction) {
    return { name: 'Action', colorThemeName: 'actionNode' };
  }
  if (handleType === NODE_HANDLE_IDS.DocumentList) {
    return { name: 'Document List', colorThemeName: 'documentListNode' };
  }
  if (handleType === NODE_HANDLE_IDS.CompanyList) {
    return { name: 'Company List', colorThemeName: 'companyListNode' };
  }
  if (handleType === NODE_HANDLE_IDS.AdvancedSettings) {
    return { name: 'Advanced Settings', colorThemeName: 'settingsNode' };
  }
  if (handleType === NODE_HANDLE_IDS.query) {
    return { name: 'Query', colorThemeName: 'executionFlowNode' };
  }

  console.log('missing generateCustomInputNodeHandle', handleType);
  return null;
};

export function getCreateNodeTypeFromHandleId(handleId, handleType) {
  if (handleType === NODE_HANDLE_TYPES.target) {
    return HANDLE_TYPE_TO_COMPONENT_LOOKUP[handleId] || null;
  }
  return null;
}

export function getAvailableCreateNodes(creationContext, connectionStartSourceNodeType) {
  if (creationContext === NODE_CREATION_CONTEXTS.ForEachAction) {
    return [NODE_TYPES.ForEachCompanyQuestionNode, NODE_TYPES.ForEachIngestWebsiteNode];
  }
  if (creationContext === NODE_CREATION_CONTEXTS.DocumentList) {
    return [NODE_TYPES.PromptNode, NODE_TYPES.IfElseNode, NODE_TYPES.FormulaNode];
  }
  if (creationContext === NODE_CREATION_CONTEXTS.CompanyList) {
    return [NODE_TYPES.PromptNode, NODE_TYPES.IfElseNode, NODE_TYPES.ForEachCompanyNode, NODE_TYPES.FormulaNode];
  }
  if (creationContext === NODE_CREATION_CONTEXTS.Start) {
    return [NODE_TYPES.PromptNode, NODE_TYPES.IfElseNode, NODE_TYPES.GenerateCompanyListNode, NODE_TYPES.FormulaNode];
  }
  if (creationContext === NODE_CREATION_CONTEXTS.BodySourceNode) {
    const defaultNodes = [
      NODE_TYPES.PromptNode,
      NODE_TYPES.IfElseNode,
      NODE_TYPES.GenerateCompanyListNode,
      NODE_TYPES.FormulaNode,
    ];

    if (connectionStartSourceNodeType === NODE_TYPES.GenerateCompanyListNode) {
      return [NODE_TYPES.ForEachCompanyNode, ...defaultNodes];
    }

    return defaultNodes;
  }
  if (creationContext === NODE_CREATION_CONTEXTS.ExecutionFlowNode) {
    const defaultNodes = [
      NODE_TYPES.PromptNode,
      NODE_TYPES.IfElseNode,
      NODE_TYPES.GenerateCompanyListNode,
      NODE_TYPES.FormulaNode,
    ];

    if (connectionStartSourceNodeType === NODE_TYPES.GenerateCompanyListNode) {
      return [NODE_TYPES.ForEachCompanyNode, ...defaultNodes];
    }

    return defaultNodes;
  }

  if (creationContext === NODE_CREATION_CONTEXTS.BodyTargetNode) {
    return [NODE_TYPES.PromptNode, NODE_TYPES.IfElseNode, NODE_TYPES.GenerateCompanyListNode, NODE_TYPES.FormulaNode];
  }
  if (creationContext === NODE_CREATION_CONTEXTS.SourceInputNode) {
    return [NODE_TYPES.DocListNode];
  }

  if (creationContext === NODE_CREATION_CONTEXTS.PlanStep) {
    return [NODE_TYPES.PlanStepNode];
  }

  if (creationContext === NODE_CREATION_CONTEXTS.OutputAggregation) {
    return [NODE_TYPES.OutputAggregationNode];
  }

  if (creationContext === NODE_CREATION_CONTEXTS.Query) {
    return [NODE_TYPES.AgentScriptQueryNode];
  }
  return [];
}

export const generateCreationContextFrom = (connectionSourceNode) => {
  const { handleParams, type } = connectionSourceNode;
  const { handleId, handleType } = handleParams;

  console.log({ handleParams, handleId, handleType });
  if (handleId === NODE_HANDLE_IDS.planStep) {
    return NODE_CREATION_CONTEXTS.PlanStep;
  }
  if (handleId === NODE_HANDLE_IDS.planStepYes) {
    return NODE_CREATION_CONTEXTS.PlanStep;
  }
  if (handleId === NODE_HANDLE_IDS.planStepNo) {
    return NODE_CREATION_CONTEXTS.PlanStep;
  }
  if (handleId === NODE_HANDLE_IDS.query) {
    return NODE_CREATION_CONTEXTS.Query;
  }
  if (handleId === NODE_HANDLE_IDS.outputAggregation) {
    return NODE_CREATION_CONTEXTS.OutputAggregation;
  }
  if (type === NODE_TYPES.AgentScriptStartNode) {
    return null;
  }

  if (type === NODE_TYPES.StartNode) {
    return NODE_CREATION_CONTEXTS.Start;
  }
  if (handleId === NODE_HANDLE_IDS.ForEachAction) {
    return NODE_CREATION_CONTEXTS.ForEachAction;
  }
  if (handleId === NODE_HANDLE_IDS.DocumentList) {
    return NODE_CREATION_CONTEXTS.DocumentList;
  }
  if (handleId === NODE_HANDLE_IDS.CompanyList) {
    return NODE_CREATION_CONTEXTS.CompanyList;
  }
  const questionNodeSourceContextTypes = [
    NODE_TYPES.StartNode,
    NODE_TYPES.PromptNode,
    NODE_TYPES.IfElseNode,
    NODE_TYPES.FormulaNode,
    NODE_TYPES.GenerateCompanyListNode,
    NODE_TYPES.ForEachCompanyNode,
  ];
  if (questionNodeSourceContextTypes.includes(type) && handleType === NODE_HANDLE_IDS.executionFlowSource) {
    return NODE_CREATION_CONTEXTS.BodySourceNode;
  }
  if (questionNodeSourceContextTypes.includes(type) && handleType === NODE_HANDLE_IDS.executionFlowTarget) {
    return NODE_CREATION_CONTEXTS.BodyTargetNode;
  }
  return null;
};

export const createFlowNode = (type) => {
  const id = nanoid(6);
  const node = {
    id,
    type,
    dragHandle: '.custom-drag-handle',
    data: {
      id,
      type,
      actions: {},
    },
  };
  return node;
};
